import { CommonModule } from '@angular/common';
import { Component, Inject, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { AgePipe } from "../shared/pipes/age.pipe";
import { MemberInsurerRestService } from '../../services/member-insurer-rest.service';
import { MemberInsurerServiceService } from '../../services/member-insurer-service.service';
import { NotificationService } from '../shared/services/notifications.service';
import { ConfirmationmodalComponent } from '../shared/components/confirmationmodal/confirmationmodal.component';
import { ConfirmationMode } from '../shared/share';

@Component({
  selector: 'app-submit-medical-evidence',
  standalone: true,
  imports: [MatIconModule,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule, AgePipe],
  templateUrl: './submit-medical-evidence.component.html',
  styleUrl: './submit-medical-evidence.component.scss'
})
export class SubmitMedicalEvidenceComponent {

  evidenceForm !: FormGroup;
  @Input() isDismissable: boolean = false;
  memberSearchControl = new FormControl('');
  filteredPatients: any[] = [];
  memberId!: string;
  copiedEmail: string | null = null;
  today: Date = new Date();
  isMobile: boolean = false;
  selectedPatient: any;
  uploadedFiles: File[] = [];
  details: any;
  documentList: any[] = [];
  isSubmitLoading: boolean = false;
  enumConfirmationMode = ConfirmationMode;
  userName: string = this.memberService.getLoginUserName();
  popupTitle = 'Submit Medical Evidence';
  popupSubtitle = 'Kindly submit the required medical evidence details for the applicant.';
  selectedUser: any = null;



  constructor(private fb: FormBuilder, public dialogRef: MatDialogRef<SubmitMedicalEvidenceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, private restService: MemberInsurerRestService, private memberService: MemberInsurerServiceService,
    private notificationService: NotificationService, private dialog: MatDialog) {
    this.evidenceForm = this.fb.group({
      reqId: [null, Validators.required],
      comments: ['']
    })

    if (data) {
      this.details = data;
      console.log('det', this.details);
      this.memberId = data.patientId; // Get memberId from dialog data
      this.evidenceForm.patchValue({
        reqId: this.details.serviceRequest.id
      });

    }

    if (this.memberId)
      this.getMemberById(this.memberId);

  }

  selectUser(user: any): void {
    if (this.selectedUser === user) {
      this.selectedUser = null;
      this.evidenceForm.patchValue({
        reqId: null
      });
    } else {
      this.selectedUser = user;
      this.getMemberById(user.patientId);
    }
  }


  getMemberById(patientId: string): void {
    this.restService.getMemberById(patientId).subscribe({
      next: (response) => {
        this.selectedPatient = response;
        this.evidenceForm.patchValue({
          reqId: this.selectedPatient.serviceRequest[0].id
        });
      },
      error: (err) => {
        console.error('Failed to fetch patient by ID:', err);
      }
    });
  }


  displayPatientName = (patient: any): string => {
    return patient ? `${patient.firstName} ${patient.lastName}` : '';
  };

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnInit() {

    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    this.memberSearchControl.valueChanges.pipe(
      debounceTime(300),
      map(value => typeof value === 'string' ? value.trim() : ''),
      tap(value => {
        if (!value || value.length < 3) {
          this.filteredPatients = []; // Clear list early
        }
      }),
      filter(value => !!value && value.length >= 3),
      distinctUntilChanged(),
      switchMap(value => this.restService.searchPatients(value.toLowerCase()))
    ).subscribe({
      next: (response) => {
        this.filteredPatients = response || [];
      },
      error: (err) => {
        console.error('Search failed:', err);
        this.filteredPatients = [];
      }
    });


  }

  onAdditional(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.addFilesToList(input.files);
      input.value = ''; // reset so same file can be uploaded again
    }
  }

  onFileDropped(event: Event) {
    event.preventDefault();
    const dragEvent = event as DragEvent;
    const files = dragEvent.dataTransfer?.files;

    if (files && files.length > 0) {
      this.addFilesToList(files);
    }
  }

  addFilesToList(fileList: FileList) {
    const filesArray = Array.from(fileList);

    // Optional: avoid duplicates by name and size
    for (const file of filesArray) {
      const alreadyExists = this.uploadedFiles.some(f =>
        f.name === file.name && f.size === file.size
      );
      if (!alreadyExists) {
        this.uploadedFiles.push(file);
      }
    }

    console.log('Current uploadedFiles:', this.uploadedFiles);
  }

  removeFile(index: number) {
    this.uploadedFiles.splice(index, 1);
  }


  async uploadFileAndStoreDocument(file: File) {
    try {
      const url = await this.getfiles3Url(file);
      console.log('File uploaded and URL received:', url);
      this.documentList.push(url);
    } catch (error) {
      console.error('Error uploading file or fetching URL:', error);
    }
  }


  getfiles3Url(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      this.restService.uploadFile(file).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  submitForm() {
    console.log('Uploaded Files:', this.uploadedFiles);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '350px';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.panelClass = 'hc';

    const dialogRef = this.dialog.open(ConfirmationmodalComponent, dialogConfig);
    dialogRef.componentInstance.title = 'Confirm';
    dialogRef.componentInstance.mode = this.enumConfirmationMode.ADD;
    dialogRef.componentInstance.message = `
    you want to submit the info filled?`;

    dialogRef.afterClosed().subscribe((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.isSubmitLoading = true;

        const formData = new FormData();
        const formValue = this.evidenceForm.value;
        const providerEntity = {
          providerId: this.memberService.getLoginUserId(),
          providerName: this.memberService.getLoginUserName()
        };

        formData.append('providerEntity', JSON.stringify(providerEntity));

        for (const key in formValue) {
          if (formValue.hasOwnProperty(key) && key !== 'reqId' && key !== 'documentType') {
            formData.append(key, formValue[key]);
          }
        }

        this.uploadedFiles.forEach((file: File) => {
          formData.append('file', file);
        });

        for (let pair of (formData as any).entries()) {
          console.log(`${pair[0]}:`, pair[1]);
        }

        this.restService.submitMedicalEvidence(formData, formValue.reqId).subscribe({
          next: (response) => {
            this.isSubmitLoading = false;
            this.notificationService.showNotification('Submitted medical Evidence successfully');
            this.memberService.emitRefresh();
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.isSubmitLoading = false;
            this.notificationService.showNotification(err.error.message, 'error');
          }
        });
      }
    });
  }



  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    this.copiedEmail = value;
    setTimeout(() => {
      this.copiedEmail = null;
    }, 1200);
  }
}
